import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRY_DIALS } from '../lib/countryDials';

const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const PhoneInput = ({ dial, number, onChange, inputClassName = '', testIdPrefix = 'phone', countryName = null }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selName, setSelName] = useState(null);

  const selected = useMemo(() => {
    if (selName) {
      const byName = COUNTRY_DIALS.find((c) => c.n === selName && c.d === dial);
      if (byName) return byName;
    }
    // países que dividem o mesmo DDI (ex: +1): usa o país escolhido no formulário
    if (countryName) {
      const byForm = COUNTRY_DIALS.find((c) => c.n === countryName && c.d === dial);
      if (byForm) return byForm;
    }
    return COUNTRY_DIALS.find((c) => c.d === dial) || COUNTRY_DIALS[0];
  }, [dial, selName, countryName]);

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    const list = [...COUNTRY_DIALS].sort((a, b) => a.n.localeCompare(b.n, 'pt-BR'));
    if (!q) return list;
    return list.filter((c) => norm(c.n).includes(q) || c.d.startsWith(q.replace('+', '')));
  }, [query]);

  const pick = (c) => {
    setSelName(c.n);
    onChange({ dial: c.d, number });
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testIdPrefix}-country-select`}
            className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white/70 px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400">
            <span className="text-base leading-none">{selected.f}</span>
            <span>+{selected.d}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testIdPrefix}-country-search`}
              placeholder="Buscar país ou código..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
          </div>
          <div className="max-h-60 overflow-y-auto" data-testid={`${testIdPrefix}-country-list`}>
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-slate-400">Nenhum país encontrado</p>
            )}
            {filtered.map((c) => (
              <button key={`${c.n}-${c.d}`} type="button" onClick={() => pick(c)}
                data-testid={`${testIdPrefix}-country-option-${c.d}`}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-orange-50 ${c.n === selected.n && c.d === selected.d ? 'bg-orange-50/70 font-semibold text-orange-700' : 'text-slate-700'}`}>
                <span className="text-base leading-none">{c.f}</span>
                <span className="flex-1 truncate">{c.n}</span>
                <span className="text-xs text-slate-400">+{c.d}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Input data-testid={`${testIdPrefix}-number-input`} value={number} inputMode="numeric"
        className={`flex-1 ${inputClassName}`}
        onChange={(e) => onChange({ dial, number: e.target.value.replace(/\D/g, '').slice(0, 14) })}
        placeholder={dial === '55' ? 'WhatsApp com DDD' : 'Número do WhatsApp'} />
    </div>
  );
};
